import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileSignature } from "lucide-react";

const STATUS_META = {
  draft:     { label: "Draft",     color: "#94A3B8", bg: "rgba(148,163,184,0.15)" },
  sent:      { label: "Sent",      color: "#38BDF8", bg: "rgba(56,189,248,0.15)" },
  accepted:  { label: "Accepted",  color: "#A78BFA", bg: "rgba(167,139,250,0.15)" },
  converted: { label: "Converted", color: "#25D366", bg: "rgba(37,211,102,0.15)" },
  rejected:  { label: "Rejected",  color: "#F87171", bg: "rgba(248,113,113,0.15)" },
  expired:   { label: "Expired",   color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};

const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

export default function Quotations() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["quotations", status, q],
    queryFn: () => api.get("/quotations", { params: { status: status === "all" ? undefined : status, search: q || undefined } }).then((r) => r.data),
  });

  const totals = useMemo(() => {
    const t = { count: quotes.length, quoted: 0, accepted: 0, converted: 0 };
    for (const it of quotes) {
      t.quoted += it.total || 0;
      if (it.status === "accepted") t.accepted += it.total || 0;
      if (it.status === "converted") t.converted += it.total || 0;
    }
    return t;
  }, [quotes]);

  return (
    <div>
      <PageHeader
        overline="Sales"
        title="Quotations"
        subtitle="Send proposals, accept online, auto-convert to invoice."
        actions={
          <Button className="rounded-sm bg-primary" onClick={() => navigate("/quotations/new")} data-testid="quotes-new">
            <Plus className="h-4 w-4 mr-2" /> New quotation
          </Button>
        }
      />
      <div className="px-6 lg:px-10 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TotalCard label="Quotations" value={totals.count} tone="neutral" />
          <TotalCard label="Total quoted" value={fmt(totals.quoted)} tone="primary" />
          <TotalCard label="Accepted" value={fmt(totals.accepted)} tone="green" />
          <TotalCard label="Converted to invoice" value={fmt(totals.converted)} tone="green" />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by quote #, customer name or email…"
              className="pl-9 rounded-sm bg-secondary/40"
              data-testid="quotes-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm bg-secondary/40 w-40" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Card className="rounded-sm bg-card border-border overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_2fr_1fr_1fr_1fr_1fr_80px] gap-3 px-4 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div>Quote #</div><div>Customer</div><div>Issue date</div><div>Valid until</div><div>Total</div><div>Linked invoice</div><div>Status</div>
          </div>
          {isLoading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && quotes.length === 0 && (
            <div className="p-12 text-center">
              <FileSignature className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">No quotations yet.</div>
              <Button onClick={() => navigate("/quotations/new")} className="rounded-sm mt-4 bg-primary" data-testid="quotes-empty-new">Create your first quotation</Button>
            </div>
          )}
          {quotes.map((qt) => {
            const meta = STATUS_META[qt.status] || STATUS_META.draft;
            return (
              <Link
                key={qt.id}
                to={`/quotations/${qt.id}`}
                className="grid grid-cols-1 md:grid-cols-[1.2fr_2fr_1fr_1fr_1fr_1fr_80px] gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors border-b border-border last:border-b-0"
                data-testid={`quote-row-${qt.id}`}
              >
                <div className="font-mono text-xs text-primary">{qt.quote_number}</div>
                <div className="text-sm truncate">{qt.bill_to?.name}<div className="text-[10px] text-muted-foreground truncate">{qt.bill_to?.email}</div></div>
                <div className="text-xs text-muted-foreground">{qt.issue_date}</div>
                <div className="text-xs text-muted-foreground">{qt.valid_until || "—"}</div>
                <div className="text-sm">{fmt(qt.total, qt.currency)}</div>
                <div className="text-xs text-primary font-mono">{qt.converted_invoice_number || "—"}</div>
                <div>
                  <Badge className="rounded-sm text-[10px] px-1.5 py-0" style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.color + "44" }}>{meta.label}</Badge>
                </div>
              </Link>
            );
          })}
        </Card>
      </div>
    </div>
  );
}

function TotalCard({ label, value, tone = "neutral" }) {
  const toneClass = {
    neutral: "text-foreground",
    primary: "text-primary",
    green: "text-green-400",
    red: "text-red-400",
  }[tone];
  return (
    <Card className="rounded-sm bg-card border-border p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${toneClass}`}>{value}</div>
    </Card>
  );
}
